<template>
  <v-app full-height>
    <!-- Snow container - alleen tijdens feestdagen én als kerstTheme actief is -->
    <div
      v-if="isHolidaySeason && isKerstTheme"
      class="snow-container"
    >
      <div
        v-for="flake in snowflakes"
        :key="flake.id"
        class="snowflake"
        :style="flake.style"
      />
    </div>

    <v-app-bar
      v-if="currentUser"
      color="primary"
      density="compact"
    >
      <v-toolbar-title>Vicoma App</v-toolbar-title>
      <v-spacer />

      <!-- Changelog button -->
      <ChangelogDialog ref="changelogDialogRef" />

      <!-- Theme Toggle Button -->
      <v-btn
        icon
        @click="toggleTheme"
      >
        <v-icon>
          <!-- Tijdens kerst: altijd kerstboom tonen -->
          {{
            isKerstTheme
              ? mdiPineTree
              : isDarkTheme
                ? mdiWeatherNight
                : mdiWeatherSunny
          }}
        </v-icon>
      </v-btn>

      <v-btn
        text
        @click="switchLocale"
      >
        {{ locale === "en" ? "EN" : "NL" }}
      </v-btn>
      <v-menu rounded>
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            x-large
          >
            <v-avatar
              color="accent"
              size="36"
            >
              <v-img
                v-if="currentUser?.graphPhoto"
                :src="currentUser?.graphPhoto"
              />
              <span v-else>{{ userAlias(currentUser) }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <app-bar-user-card />
      </v-menu>
    </v-app-bar>
    <v-navigation-drawer
      v-if="currentUser"
      expand-on-hover
      rail
      permanent
      :width="280"
      @update:rail="handleRailUpdate"
    >
      <v-list
        nav
        density="compact"
      >
        <v-list-item
          :title="t('nav.people')"
          :prepend-icon="mdiViewDashboard"
          rounded="xl"
          to="/"
        />

        <v-list-item
          :title="t('nav.timesheet')"
          :prepend-icon="mdiTimetable"
          rounded="xl"
          to="/timesheet"
        />
        <v-list-item
          v-if="userIsAdmin"
          :title="t('nav.leave')"
          :prepend-icon="mdiIsland"
          rounded="xl"
          to="/leave"
        />
        <v-divider class="my-2" />
        <v-list-subheader v-if="!drawerIsRail">
          {{ t('nav.projects').toUpperCase() }}
        </v-list-subheader>

        <v-list-item
          :title="t('nav.myProjects')"
          :prepend-icon="mdiClipboardListOutline"
          rounded="xl"
          to="/projects"
        />

        <v-list-item
          :title="t('nav.projectIntake')"
          :prepend-icon="mdiClipboardPlusOutline"
          :disabled="!userIsProjectIntake && !userIsAdmin"
          rounded="xl"
          to="/project-intake"
        >
          <template
            v-if="!userIsProjectIntake && !userIsAdmin"
            #append
          >
            <v-icon
              :icon="mdiLockOutline"
              size="small"
            />
          </template>
        </v-list-item>

        <!-- Compass: alleen zichtbaar voor de Compass-groep -->
        <v-list-item
          v-if="userIsCompass"
          :title="t('nav.compass')"
          :prepend-icon="mdiCompassOutline"
          rounded="xl"
          to="/compass"
        />

        <!-- Project Administratie sectie -->
        <v-divider class="my-2" />
        <v-list-subheader v-if="!drawerIsRail">
          {{ t('nav.projectAdmin').toUpperCase() }}
        </v-list-subheader>

        <v-list-item
          :title="t('nav.dashboard')"
          :prepend-icon="mdiChartBox"
          :disabled="!userIsAdmin"
          rounded="xl"
          to="/dashboard"
        >
          <template
            v-if="!userIsAdmin"
            #append
          >
            <v-icon
              :icon="mdiLockOutline"
              size="small"
            />
          </template>
        </v-list-item>

        <v-list-item
          :title="t('nav.incompleteTimeEntries')"
          :prepend-icon="mdiClockAlertOutline"
          :disabled="!(userIsAdmin || userIsTimesheetAdmin)"
          rounded="xl"
          to="/incomplete-timesheets"
        >
          <template
            v-if="!(userIsAdmin || userIsTimesheetAdmin)"
            #append
          >
            <v-icon
              :icon="mdiLockOutline"
              size="small"
            />
          </template>
        </v-list-item>

        <v-divider class="my-2" />
        <v-list-subheader v-if="!drawerIsRail">
          {{ t('nav.helpSupport').toUpperCase() }}
        </v-list-subheader>

        <v-list-item
          :title="t('nav.help')"
          :prepend-icon="mdiHelpCircleOutline"
          rounded="xl"
          to="/help"
        />

        <v-list-item
          :title="t('nav.feedback')"
          :prepend-icon="mdiMessageTextOutline"
          rounded="xl"
          :to="{ path: '/feedback', query: { from: currentRoute.path } }"
        />

        <v-list-item
          v-if="userIsAdmin"
          :title="t('nav.adminDocumentation')"
          :prepend-icon="mdiFileDocumentOutline"
          rounded="xl"
          to="/admin-documentation"
        />

        <v-list-item
          v-if="showInstallButton"
          :title="t('nav.installApp')"
          rounded="xl"
          @click="installApp"
        >
          <template #prepend>
            <v-badge
              color="error"
              dot
            >
              <v-icon :icon="mdiDownload" />
            </v-badge>
          </template>
        </v-list-item>
      </v-list>
      <template
        v-if="!drawerIsRail"
        #append
      >
        <div
          class="pa-2"
          align="center"
        >
          <v-chip
            class="bg-vicyellow opacity-100"
            size="small"
          >
            Vicoma {{ new Date().getFullYear() }} | Built by Tim Baljon
          </v-chip>
          <v-chip
            variant="text"
            size="x-small"
            class="mt-0"
          >
            v{{ appVersion }} (build {{ buildNumber }})
          </v-chip>
        </div>
      </template>
    </v-navigation-drawer>
    <v-main
      class="pa-0"
      style="display: flex; flex-direction: column; height: 100%"
    >
      <!-- <v-container fluid> -->
      <router-view
        v-if="currentUser"
        v-slot="{ Component }"
      >
        <v-fade-transition hide-on-leave>
          <component :is="Component" />
        </v-fade-transition>
      </router-view>
      <v-row
        v-else
        class="center"
      >
        <v-btn
          v-if="!isLoginInProgress()"
          color="primary"
          @click="handleLogin"
        >
          Login
        </v-btn>
        <v-progress-circular
          v-else
          color="primary"
          indeterminate
        />
      </v-row>
      <!-- </v-container> -->
    </v-main>

    <!-- Update Notification -->
    <UpdateNotification
      :show="showUpdatePrompt"
      @update="handleAppUpdate"
      @dismiss="showUpdatePrompt = false"
    />

    <!-- Feedbackvraag bij openen (hooguit eens per 30 dagen) -->
    <FeedbackPrompt v-if="currentUser" />
  </v-app>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { ref, computed, onMounted, watch } from "vue";
import { useTheme } from "vuetify";
import { useRegisterSW } from 'virtual:pwa-register/vue'
import {
  login,
  logout,
  getCurrentAccount,
  fetchProfilePhoto,
  isLoginInProgress,
} from "@/plugins/msal";
import { useRepo } from "pinia-orm";
import AppBarUserCard from "@/components/AppBarUserCard.vue";
import type { UserInfo } from "./types";
import Account from "./models/Account";
import {
  mdiWeatherNight,
  mdiWeatherSunny,
  mdiViewDashboard,
  mdiTimetable,
  mdiClipboardListOutline,
  mdiIsland,
  mdiHelpCircleOutline,
  mdiEmailOutline,
  mdiClockAlertOutline,
  mdiClipboardPlusOutline,
  mdiLockOutline,
  mdiDownload,
  mdiPineTree, // kerstboom icoon
  mdiChartBox,
  mdiFileDocumentOutline,
  mdiMessageTextOutline,
  mdiCompassOutline,
} from "@mdi/js";
import { useUserRoles } from '@/composables/useUserRoles'
import { useAnalytics } from '@/composables/useAnalytics'
import ChangelogDialog from '@/components/ChangelogDialog.vue';

// PWA Update registration
const {
  needRefresh,
  updateServiceWorker,
} = useRegisterSW({
  onRegistered(registration) {
    console.log('Service Worker registered:', registration)
  },
  onRegisterError(error) {
    console.error('Service Worker registration error:', error)
  },
})

const showUpdatePrompt = computed(() => needRefresh.value)

const handleAppUpdate = async () => {
  await updateServiceWorker(true)
}

// Huidige pagina, meegegeven aan /feedback zodat feedback de context bevat
const currentRoute = useRoute();
const accountRepo = useRepo(Account);
const currentUser = computed(() => accountRepo.first());
const { t, locale } = useI18n();
const {
  trackThemeToggled,
  trackLanguageSwitched,
  trackLogin,
  trackLogout,
  trackChangelogViewed
} = useAnalytics();

const drawerIsRail = ref(true);
const deferredPrompt = ref<any>(null);
const showInstallButton = ref(false);
const isHolidaySeason = ref(false);
const snowflakes = ref<{ id: number; style: Record<string, string> }[]>([]);

// Register install prompt listener IMMEDIATELY (before onMounted)
// This event only fires ONCE, so we need to catch it early
if (window.matchMedia('(display-mode: standalone)').matches) {
  console.log('App is already installed');
  showInstallButton.value = false;
} else {
  console.log('App is not installed, listening for install prompt');
}

window.addEventListener('beforeinstallprompt', (e) => {
  console.log('beforeinstallprompt event fired');
  e.preventDefault();
  deferredPrompt.value = e;
  showInstallButton.value = true;
  console.log('Install button should now be visible (showInstallButton = true)');
});

// Hide button after successful installation
window.addEventListener('appinstalled', () => {
  console.log('App successfully installed');
  showInstallButton.value = false;
  deferredPrompt.value = null;
});

// Gebruik de composable
const { userIsAdmin, userIsTimesheetAdmin, userIsLeaveAdmin, userIsProjectIntake, userIsCompass, resolveAndSetRoles, clearRoles } = useUserRoles()

// Get app version and build number from environment variables
const appVersion = import.meta.env.VITE_APP_VERSION || "0.0.0";
const buildNumber = import.meta.env.VITE_BUILDNUMBER || "0";

// Theme management
const theme = useTheme();
const isDarkTheme = computed(
  () => theme.global.name.value === "myCustomDarkTheme"
);
const isKerstTheme = computed(
  () => theme.global.name.value === "kerstTheme"
);

const setAgGridTheme = (isDark: boolean) => {
  const agThemeMode = isDark ? "dark" : "light";
  document.body.dataset.agThemeMode = agThemeMode;
};

// Theme volgorde toggle:
// - als kerstTheme actief: toggle -> myCustomDarkTheme
// - als dark actief: toggle -> myCustomLightTheme
// - als light actief: toggle -> myCustomDarkTheme
const toggleTheme = () => {
  if (isKerstTheme.value) {
    theme.global.name.value = "myCustomDarkTheme";
    setAgGridTheme(true);
    trackThemeToggled('dark');
    return;
  }

  const next =
    theme.global.name.value === "myCustomDarkTheme"
      ? "myCustomLightTheme"
      : "myCustomDarkTheme";

  theme.global.name.value = next;
  setAgGridTheme(next === "myCustomDarkTheme");
  trackThemeToggled(next === "myCustomDarkTheme" ? 'dark' : 'light');
};

const handleLogin = async () => {
  const account = await login();
  if (account) {
    trackLogin();
    // Check admin status after login
    await checkAdminStatus();
  }
};

const handleLogout = () => {
  trackLogout();
  logout();
  accountRepo.flush();
  clearRoles() // Gebruik de clear functie
};

const userAlias = (user: Partial<UserInfo>) => {
  return user?.username?.split("@")[0] || "";
};

const handleRailUpdate = (isRail: boolean) => {
  drawerIsRail.value = isRail;
};

const checkAdminStatus = async () => {
  if (!currentUser.value?.localAccountId) return;

  try {
    // Eén Graph-call voor alle groepslidmaatschappen tegelijk
    await resolveAndSetRoles();
  } catch (error) {
    console.error("Error checking admin status:", error);
    clearRoles();
  }
};

const installApp = async () => {
  if (!deferredPrompt.value) {
    console.log('No deferred prompt available');
    return;
  }

  try {
    console.log('Showing install prompt');
    deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;

    console.log('Install prompt outcome:', outcome);
    if (outcome === 'accepted') {
      showInstallButton.value = false;
    }
  } catch (error) {
    console.error('Error during installation:', error);
  } finally {
    deferredPrompt.value = null;
  }
};

// Debug: Watch currentUser changes to see if button should be visible
watch(currentUser, (newVal) => {
  console.log('currentUser changed:', newVal ? 'LOGGED IN' : 'LOGGED OUT');
  console.log('showInstallButton state:', showInstallButton.value);
  console.log('deferredPrompt available:', !!deferredPrompt.value);
});

onMounted(async () => {
  // persist language on load
  const saved = localStorage.getItem("locale");
  if (saved) locale.value = saved;

  // Get account FIRST to avoid MSAL race condition
  const account = await getCurrentAccount();
  
  if (account) {
    const newAccount = new Account(account);
    newAccount.graphPhoto = await fetchProfilePhoto(account.localAccountId);
    accountRepo.fresh(newAccount);

    // Check admin status
    await checkAdminStatus();
  }

  // 15 dec t/m 5 jan
  const today = new Date();
  const month = today.getMonth();
  const day = today.getDate();
  isHolidaySeason.value =
    (month === 11 && day >= 15) || (month === 0 && day <= 5);

  if (isHolidaySeason.value) {
    // Gebruik kersttheme als default tijdens holidays
    theme.global.name.value = "kerstTheme";
    setAgGridTheme(true); // dark base
  } else {
    // respecteer bestaande default (licht/donker) maar sync ag-grid
    setAgGridTheme(isDarkTheme.value);
  }

  // Sneeuw alleen genereren als het kersttheme ook echt actief is
  if (isHolidaySeason.value && isKerstTheme.value) {
    const flakeCount = 100;
    const flakes: { id: number; style: Record<string, string> }[] = [];
    for (let i = 0; i < flakeCount; i++) {
      const size = Math.random() * 6 + 2;
      const left = Math.random() * 100;
      const delay = Math.random() * 10;
      const duration = 8 + Math.random() * 12;
      const useDiagonal = Math.random() < 0.5;

      flakes.push({
        id: i,
        style: {
          width: `${size}px`,
          height: `${size}px`,
          left: `${left}vw`,
          top: `-${size}px`,
          animationName: useDiagonal ? "diagonal-fall" : "fall",
          animationDuration: `${duration}s`,
          animationDelay: `${delay}s`,
          animationIterationCount: "infinite",
          animationTimingFunction: "linear",
        },
      });
    }
    snowflakes.value = flakes;
  } else {
    // geen sneeuw buiten kersttheme
    snowflakes.value = [];
  }
});

// language switcher
const switchLocale = () => {
  const next = locale.value === "en" ? "nl" : "en";
  trackLanguageSwitched(next);
  locale.value = next;
  localStorage.setItem("locale", next);
};
</script>
<style>
/* .v-application__wrap {
  min-height: 100dvh !important;
} */
.center {
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

/* Mooie snowflakes */
.snow-container {
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  pointer-events: none;
}

.snowflake {
  position: absolute;
  background-color: white;
  border-radius: 50%;
  opacity: 0.8;
  pointer-events: none;
}

@keyframes fall {
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
    transform: translateY(100vh);
  }
}

@keyframes diagonal-fall {
  0% {
    opacity: 0;
    transform: translate(0, 0);
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 0.25;
    transform: translate(10vw, 100vh);
  }
}
</style>
